/**
 * Strategy Lab — walk-forward backtests of the validated strategy sleeves
 * (Big Cap CORE / Big Cap Champion / Mid-Small / Market Neutral).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FlaskConical, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { useAuth } from "../App";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { API } from "../components/stats/statsApi";

const SLEEVE_COLORS = {
  big_core: { accent: "text-emerald-300", line: "#34d399", active: "bg-emerald-500/25 text-emerald-300" },
  big_champion: { accent: "text-indigo-300", line: "#818cf8", active: "bg-indigo-500/25 text-indigo-300" },
  mid_small: { accent: "text-amber-300", line: "#fbbf24", active: "bg-amber-500/25 text-amber-300" },
  market_neutral_opt: { accent: "text-fuchsia-300", line: "#e879f9", active: "bg-fuchsia-500/25 text-fuchsia-300" },
};

const pct = (v, dp = 1) =>
  v == null ? "—" : `${v > 0 ? "+" : ""}${(v * 100).toFixed(dp)}%`;
const num = (v) => (v == null ? "—" : v);

const StatCard = ({ label, value, sub, accent }) => (
  <Card>
    <CardContent className="p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold font-mono ${accent || ""}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </CardContent>
  </Card>
);

export const RetCell = ({ v, dp = 1 }) => (
  <td className={`py-1.5 px-2 text-right font-mono ${
    v > 0 ? "text-green-400" : v < 0 ? "text-red-400" : "text-muted-foreground"}`}>
    {pct(v, dp)}
  </td>
);

const EquityChart = ({ equity, color, label }) => (
  <div className="h-72">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={equity} margin={{ top: 6, right: 12, bottom: 0, left: 0 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8b8fa3" }}
               tickFormatter={(d) => d?.slice(2, 7)} minTickGap={40} />
        <YAxis tick={{ fontSize: 10, fill: "#8b8fa3" }} domain={["auto", "auto"]}
               tickFormatter={(v) => v.toFixed(0)} width={44} />
        <Tooltip
          contentStyle={{ background: "#12141c", border: "1px solid rgba(255,255,255,0.1)",
                          borderRadius: 8, fontSize: 12 }}
          formatter={(v, name) => [v?.toFixed(1), name]} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Line type="monotone" dataKey="strategy" name={label} stroke={color}
              strokeWidth={2} dot={false} />
        <Line type="monotone" dataKey="spy" name="SPY" stroke="#6b7280"
              strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

const BookTable = ({ items, side }) => (
  <table className="w-full text-xs">
    <thead className="border-b border-border text-muted-foreground">
      <tr>
        <th className="py-1 px-2 text-left">#</th>
        <th className="py-1 px-2 text-left">Symbol</th>
        <th className="py-1 px-2 text-right">Weight</th>
        <th className="py-1 px-2 text-left">Sector</th>
      </tr>
    </thead>
    <tbody>
      {items.map((r, i) => (
        <tr key={r.symbol} className="border-b border-border/40">
          <td className="py-1 px-2 text-muted-foreground">{i + 1}</td>
          <td className="py-1 px-2 font-semibold">{r.symbol}</td>
          <td className={`py-1 px-2 text-right font-mono ${
            side === "short" ? "text-red-400" : "text-emerald-400"}`}>
            {(r.weight * 100).toFixed(2)}%
          </td>
          <td className="py-1 px-2 text-muted-foreground">{r.sector || "—"}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const RiskMonitor = ({ token }) => {
  const [rm, setRm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (refresh = false) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/strategy-lab/risk-monitor${refresh ? "/refresh" : ""}`,
        { method: refresh ? "POST" : "GET", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setRm(await res.json());
    } catch { /* noop */ } finally { setBusy(false); }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  if (!rm) return null;
  const order = Object.keys(rm.sleeves);
  const anyAlert = order.some((k) => rm.sleeves[k].status !== "ok");
  return (
    <Card className={anyAlert ? "border-amber-500/40" : "border-emerald-500/20"}
          data-testid="risk-monitor">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <ShieldAlert className={`w-4 h-4 ${anyAlert ? "text-amber-400" : "text-emerald-400"}`} />
          Daily risk monitor
          <span className="text-muted-foreground font-normal font-mono text-xs">
            prices {rm.price_date} · runs 07:30 UTC · drift bands β ±{rm.thresholds.beta} / sector ±{(rm.thresholds.sector * 100).toFixed(0)}% / short cover +{(rm.thresholds.short_cover * 100).toFixed(0)}%
          </span>
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={() => load(true)} disabled={busy}
                data-testid="risk-monitor-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          {order.map((k) => {
            const s = rm.sleeves[k];
            return (
              <div key={k} data-testid={`risk-sleeve-${k}`}
                   className={`rounded-md border p-2 text-xs ${
                     s.status === "alert" ? "border-red-500/50 bg-red-500/5"
                       : s.status === "watch" ? "border-amber-500/40 bg-amber-500/5"
                       : "border-border bg-muted/20"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold truncate">{s.label}</span>
                  <span className={`w-2 h-2 rounded-full ${
                    s.status === "alert" ? "bg-red-500" : s.status === "watch" ? "bg-amber-400" : "bg-emerald-500"}`} />
                </div>
                <div className="font-mono text-muted-foreground mt-1">
                  <span className={s.pnl_pct >= 0 ? "text-green-400" : "text-red-400"}>
                    {pct(s.pnl_pct)}
                  </span>{" "}since rebal · {s.days_held}d · β {s.beta.toFixed(2)} · net {(s.net * 100).toFixed(0)}%
                </div>
                {s.macro_nets && (
                  <div className="font-mono text-[10px] text-muted-foreground mt-1 flex flex-wrap gap-x-2"
                       data-testid={`risk-macro-nets-${k}`}>
                    {Object.entries(s.macro_nets).map(([f, v]) => (
                      <span key={f} className={Math.abs(v) > 0.1 ? "text-amber-400" : ""}>
                        {f.slice(0, 4)} {(v * 100).toFixed(0)}%
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {order.flatMap((k) => rm.sleeves[k].alerts.map((a, i) => (
          <div key={k + i} className={`text-xs font-mono px-2 py-1 rounded ${
            a.level === "critical" ? "bg-red-500/10 text-red-400" : "bg-amber-500/10 text-amber-400"}`}>
            [{rm.sleeves[k].label}] {a.msg}
          </div>
        )))}
      </CardContent>
    </Card>
  );
};

const FactorBar = ({ label, v, scale }) => {
  const pct = v == null ? 0 : Math.min(Math.abs(v) / scale, 1) * 50;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-20 text-muted-foreground capitalize">{label}</span>
      <div className="flex-1 h-3.5 relative bg-muted/40 rounded-sm overflow-hidden">
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border" />
        {v != null && (
          <div
            className={`absolute top-0.5 bottom-0.5 rounded-sm ${v >= 0 ? "bg-emerald-500/70" : "bg-red-500/70"}`}
            style={v >= 0
              ? { left: "50%", width: `${pct}%` }
              : { right: "50%", width: `${pct}%` }} />
        )}
      </div>
      <span className={`w-14 text-right font-mono ${
        v == null ? "text-muted-foreground" : v >= 0 ? "text-emerald-400" : "text-red-400"}`}>
        {v == null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(2)}`}
      </span>
    </div>
  );
};

const ExposureChip = ({ label, value, accent }) => (
  <div className="px-2.5 py-1.5 rounded-md border border-border bg-muted/20">
    <span className="text-[10px] uppercase tracking-wide text-muted-foreground mr-2">{label}</span>
    <span className={`text-xs font-mono font-semibold ${accent || ""}`}>{value}</span>
  </div>
);

export const SPY_SECTOR_W = {
  Technology: 0.355, "Financial Services": 0.13, "Communication Services": 0.105,
  "Consumer Cyclical": 0.10, Healthcare: 0.095, Industrials: 0.085,
  "Consumer Defensive": 0.05, Energy: 0.028, Utilities: 0.025,
  "Real Estate": 0.018, "Basic Materials": 0.016,
};

export const ExposuresPanel = ({ ex, sleeveKey, vsSpy = false }) => {
  if (!ex) return null;
  const maxSec = Math.max(0.05, ...ex.sectors.map((s) => Math.max(s.long, -s.short)));
  const styleScale = Math.max(0.5, ...Object.values(ex.styles || {}).map((v) => Math.abs(v || 0)));
  let sectorRows = ex.sectors;
  if (vsSpy) {
    const held = new Set(ex.sectors.map((s) => s.sector));
    sectorRows = [...ex.sectors, ...Object.keys(SPY_SECTOR_W)
      .filter((k) => !held.has(k))
      .map((k) => ({ sector: k, long: 0, short: 0, net: 0, _unheld: true }))];
  }
  return (
    <div className="space-y-3" data-testid={`exposures-${sleeveKey}`}>
      <div className="flex gap-2 flex-wrap">
        <ExposureChip label="Long" value={`${(ex.long_exposure * 100).toFixed(1)}%`} accent="text-emerald-400" />
        <ExposureChip label="Short" value={`${(ex.short_exposure * 100).toFixed(1)}%`} accent="text-red-400" />
        {ex.spy_hedge !== 0 && (
          <ExposureChip label="SPY hedge" value={`${(ex.spy_hedge * 100).toFixed(1)}%`} accent="text-cyan-400" />
        )}
        <ExposureChip label="Net" value={`${(ex.net * 100).toFixed(1)}%`} />
        <ExposureChip label="Gross" value={`${ex.gross.toFixed(2)}x`} />
        <ExposureChip label="Positions" value={`${ex.n_long}L / ${ex.n_short}S`} />
        <ExposureChip label="Max pos" value={ex.max_position != null ? `${(ex.max_position * 100).toFixed(1)}%` : "—"} />
        <ExposureChip label="Top-10 conc." value={ex.top10_pct_of_gross != null ? `${(ex.top10_pct_of_gross * 100).toFixed(0)}% of gross` : "—"} />
        {ex.beta_ex_ante != null && (
          <ExposureChip label={vsSpy ? "β ex-ante (SPY = 1.00)" : "β ex-ante"}
                        value={ex.beta_ex_ante.toFixed(2)}
                        accent={vsSpy
                          ? (ex.beta_ex_ante > 1.5 ? "text-amber-400" : "text-sky-400")
                          : (Math.abs(ex.beta_ex_ante) <= 0.1 ? "text-emerald-400" : "text-amber-400")} />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Sector exposure{vsSpy && <span className="text-muted-foreground font-normal"> — vs SPY weights</span>}</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-xs" data-testid={`sector-exposure-${sleeveKey}`}>
              <thead className="border-b border-border text-muted-foreground">
                <tr>
                  <th className="py-1 px-2 text-left">Sector</th>
                  <th className="py-1 px-2 text-right">Long</th>
                  <th className="py-1 px-2 text-right">Short</th>
                  <th className="py-1 px-2 w-32"></th>
                  <th className="py-1 px-2 text-right">Net</th>
                  {vsSpy && <th className="py-1 px-2 text-right">SPY</th>}
                  {vsSpy && <th className="py-1 px-2 text-right">Δ vs SPY</th>}
                </tr>
              </thead>
              <tbody>
                {sectorRows.map((s) => {
                  const spyW = SPY_SECTOR_W[s.sector];
                  const delta = spyW != null ? s.net - spyW : null;
                  return (
                  <tr key={s.sector} className={`border-b border-border/40 ${s._unheld ? "opacity-50" : ""}`}>
                    <td className="py-1 px-2 whitespace-nowrap">{s.sector}</td>
                    <td className="py-1 px-2 text-right font-mono text-emerald-400">
                      {s.long ? `${(s.long * 100).toFixed(1)}%` : "—"}
                    </td>
                    <td className="py-1 px-2 text-right font-mono text-red-400">
                      {s.short ? `${(s.short * 100).toFixed(1)}%` : "—"}
                    </td>
                    <td className="py-1 px-2">
                      <div className="h-3 relative bg-muted/40 rounded-sm overflow-hidden">
                        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border" />
                        <div className="absolute top-0 bottom-0 bg-emerald-500/60"
                             style={{ left: "50%", width: `${Math.min(s.long / maxSec, 1) * 50}%` }} />
                        <div className="absolute top-0 bottom-0 bg-red-500/60"
                             style={{ right: "50%", width: `${Math.min(-s.short / maxSec, 1) * 50}%` }} />
                      </div>
                    </td>
                    <td className={`py-1 px-2 text-right font-mono ${
                      s.net > 0.001 ? "text-emerald-400" : s.net < -0.001 ? "text-red-400" : "text-muted-foreground"}`}>
                      {(s.net * 100).toFixed(1)}%
                    </td>
                    {vsSpy && (
                      <td className="py-1 px-2 text-right font-mono text-muted-foreground">
                        {spyW != null ? `${(spyW * 100).toFixed(1)}%` : "—"}
                      </td>
                    )}
                    {vsSpy && (
                      <td className={`py-1 px-2 text-right font-mono ${
                        delta == null ? "text-muted-foreground"
                          : delta > 0.10 ? "text-amber-400"
                          : delta < -0.10 ? "text-sky-400" : "text-muted-foreground"}`}
                          data-testid={`sector-delta-${sleeveKey}-${s.sector.replace(/\s+/g, "-").toLowerCase()}`}>
                        {delta != null ? `${delta >= 0 ? "+" : ""}${(delta * 100).toFixed(1)}pp` : "—"}
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Factor exposure <span className="text-muted-foreground font-normal">(Barra, weighted z-units)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2" data-testid={`factor-exposure-${sleeveKey}`}>
            {ex.styles ? (
              <>
                {Object.entries(ex.styles).map(([k, v]) => (
                  <FactorBar key={k} label={k} v={v} scale={styleScale} />
                ))}
                <p className="text-[10px] text-muted-foreground pt-2">
                  Weighted sum of cross-sectional z-score exposures (Barra v2, point-in-time).
                  For long-only books a positive value means the book tilts above the market
                  median on that factor; for L/S books it is the net factor bet. β ex-ante uses
                  raw 252d betas incl. any SPY overlay.
                  {vsSpy && " SPY reference: β = 1.00 and every style tilt ≈ 0 by construction — each bar reads directly as the book's tilt versus the index."}
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">No factor exposures available (R2 archive not reachable).</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Sleeve = ({ s, sleeveKey }) => {
  const c = SLEEVE_COLORS[sleeveKey];
  const st = s.stats || {};
  const isLS = sleeveKey.startsWith("market_neutral");
  return (
    <div className="space-y-5" data-testid={`sleeve-${sleeveKey}`}>
      <div>
        <p className="text-sm text-muted-foreground max-w-3xl">{s.description}</p>
        <p className="text-[11px] font-mono text-muted-foreground/70 mt-1">{s.config}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid={`stats-${sleeveKey}`}>
        <StatCard label="Total return" value={pct(st.total_return)} accent={c.accent}
                  sub={`SPY ${pct(st.spy_total)} · ${st.months} mo`} />
        <StatCard label="Annualized" value={pct(st.ann_return)} accent={c.accent}
                  sub={`vol ${pct(st.ann_vol)}`} />
        <StatCard label="Sharpe" value={num(st.sharpe)}
                  sub={`excess ${pct(st.excess_mo, 2)}/mo · t ${num(st.t_stat)}`} />
        <StatCard label="Max drawdown" value={pct(st.max_dd)}
                  sub={`worst mo ${pct(st.worst_month)}`} />
        <StatCard label="Hit rate" value={st.hit_rate != null ? `${(st.hit_rate * 100).toFixed(0)}%` : "—"}
                  sub={`best mo ${pct(st.best_month)}`} />
        <StatCard label="Gross leverage" value={st.gross_leverage != null ? `${st.gross_leverage}x` : "—"}
                  sub="avg Σ|w|" />
        <StatCard label="Net exposure" value={st.net_exposure != null ? `${(st.net_exposure * 100).toFixed(0)}%` : "—"}
                  sub="avg Σw incl. hedge" />
        <StatCard label="Realized beta" value={num(st.realized_beta)}
                  sub="monthly rets vs SPY" />
        <StatCard label="Avg positions" value={num(st.avg_positions)} />
        <StatCard label="Turnover (of book)"
                  value={st.avg_book_turnover != null
                    ? `${(st.avg_book_turnover * 100).toFixed(0)}%/mo`
                    : st.avg_turnover != null ? `${(st.avg_turnover * 100).toFixed(0)}%/mo` : "—"}
                  sub={isLS && st.avg_book_turnover_long != null
                    ? `L ${(st.avg_book_turnover_long * 100).toFixed(0)}% · S ${st.avg_book_turnover_short != null ? (st.avg_book_turnover_short * 100).toFixed(0) : "—"}% of side · NAV ${st.avg_turnover != null ? (st.avg_turnover * 100).toFixed(0) : "—"}%`
                    : `${st.avg_pos_churn != null ? (st.avg_pos_churn * 100).toFixed(0) : "—"}% names replaced · NAV ${st.avg_turnover != null ? (st.avg_turnover * 100).toFixed(0) : "—"}%`} />
        <StatCard label="Holding period"
                  value={st.holding_period_months_long != null
                    ? `${isLS ? "L " : ""}${st.holding_period_months_long} mo`
                    : "—"}
                  sub={isLS
                    ? `S ${st.holding_period_months_short ?? "—"} mo · avg position spell`
                    : "avg position spell"} />
        <StatCard label={`Net of costs (${st.cost_bps ?? 2}bps)`} value={pct(st.total_return_net)}
                  accent={c.accent}
                  sub={`Sharpe ${num(st.sharpe_net)} · avg ${st.avg_cost_mo != null ? (st.avg_cost_mo * 10000).toFixed(1) : "—"}bps/mo`} />
        {st.shorts_covered_total > 0 && (
          <StatCard label="Gap-covers" value={st.shorts_covered_total}
                    sub="shorts covered at +40%" />
        )}
      </div>

      <Card>
        <CardHeader className="pb-1">
          <CardTitle className="text-sm">Equity curve (base 100)</CardTitle>
        </CardHeader>
        <CardContent>
          <EquityChart equity={s.equity} color={c.line} label={s.label} />
        </CardContent>
      </Card>

      <div>
        <div className="text-[11px] uppercase font-semibold text-muted-foreground mb-2">
          Current exposures — as of {s.latest_book?.as_of}
        </div>
        <ExposuresPanel ex={s.latest_book?.exposures} sleeveKey={sleeveKey} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Monthly performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-xs" data-testid={`monthly-${sleeveKey}`}>
                <thead className="border-b border-border text-muted-foreground sticky top-0 bg-card">
                  <tr>
                    <th className="py-1.5 px-2 text-left">Month</th>
                    <th className="py-1.5 px-2 text-right">Strategy</th>
                    <th className="py-1.5 px-2 text-right">Net</th>
                    <th className="py-1.5 px-2 text-right">SPY</th>
                    <th className="py-1.5 px-2 text-right">Excess</th>
                    <th className="py-1.5 px-2 text-right">Pos</th>
                    <th className="py-1.5 px-2 text-right">Gross</th>
                    <th className="py-1.5 px-2 text-right">Net exp</th>
                  </tr>
                </thead>
                <tbody>
                  {[...s.monthly].reverse().map((m) => (
                    <tr key={m.month + m.obs} className="border-b border-border/40">
                      <td className="py-1.5 px-2 font-mono whitespace-nowrap">
                        {m.month}
                        {m.shorts_covered > 0 && (
                          <span className="ml-1 text-amber-400" title={`${m.shorts_covered} short(s) gap-covered at +40%`}>
                            ⚠{m.shorts_covered}
                          </span>
                        )}
                      </td>
                      <RetCell v={m.ret} />
                      <RetCell v={m.ret_net} />
                      <RetCell v={m.spy} />
                      <RetCell v={m.excess} />
                      <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">{m.n_pos}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">{m.gross}x</td>
                      <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">
                        {(m.net * 100).toFixed(0)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Current book <span className="text-muted-foreground font-normal">
                as of {s.latest_book?.as_of}</span>
              {isLS && s.latest_book?.spy_hedge != null && (
                <Badge variant="outline" className="ml-2 text-[10px] font-mono">
                  SPY hedge {(s.latest_book.spy_hedge * 100).toFixed(1)}%
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-y-auto space-y-4">
              <BookTable items={s.latest_book?.longs || []} side="long" />
              {(s.latest_book?.shorts || []).length > 0 && (
                <>
                  <div className="text-[10px] uppercase font-semibold text-red-400">
                    Shorts ({s.latest_book.shorts.length})
                  </div>
                  <BookTable items={s.latest_book.shorts} side="short" />
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default function StrategyLabPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [sleeve, setSleeve] = useState("big_core");
  const pollRef = useRef(null);

  const fetchResults = useCallback(async () => {
    try {
      const res = await fetch(`${API}/strategy-lab/results`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setData(await res.json());
    } catch { /* noop */ }
  }, [token]);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/strategy-lab/status`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) return await res.json();
    } catch { /* noop */ }
    return null;
  }, [token]);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const st = await fetchStatus();
      setStatus(st);
      if (st?.status === "done" || st?.status === "error") {
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (st.status === "done") { toast.success("Backtest complete"); fetchResults(); }
        else toast.error(`Backtest failed: ${st.step}`);
      }
    }, 4000);
  }, [fetchStatus, fetchResults]);

  useEffect(() => {
    if (!token) return;
    fetchResults();
    fetchStatus().then((st) => {
      setStatus(st);
      if (st?.status === "running") startPolling();
    });
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [token, fetchResults, fetchStatus, startPolling]);

  const run = async () => {
    try {
      const res = await fetch(`${API}/strategy-lab/run`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json()).detail || res.status);
      setStatus({ status: "running", step: "starting" });
      toast.info("Backtest started — this can take several minutes");
      startPolling();
    } catch (e) {
      toast.error(`${e.message}`);
    }
  };

  const running = status?.status === "running";
  const order = data?.order || Object.keys(SLEEVE_COLORS);
  const current = data?.strategies?.[sleeve];

  return (
    <div className="p-6 space-y-5" data-testid="strategy-lab-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-primary" /> Strategy Lab
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Walk-forward monthly backtests of the validated sleeves on a common window
            {data?.window && (
              <span className="font-mono"> ({data.window.start} → {data.window.end},
                {" "}{data.window.rebalances} rebalances)</span>
            )}. Books are replayed point-in-time; returns are gross of costs.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {running && (
            <span className="text-xs text-muted-foreground font-mono animate-pulse"
                  data-testid="lab-progress">
              {status?.step || "running…"}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={run} disabled={running}
                  data-testid="lab-run-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${running ? "animate-spin" : ""}`} />
            {running ? "Running…" : data ? "Re-run backtest" : "Run backtest"}
          </Button>
        </div>
      </div>

      {data && <RiskMonitor token={token} />}

      {data && (
        <div className="flex gap-1 border border-border rounded-md p-0.5 w-fit flex-wrap">
          {order.map((k) => (
            <button key={k}
                    data-testid={`sleeve-tab-${k}`}
                    onClick={() => setSleeve(k)}
                    className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${
                      sleeve === k ? SLEEVE_COLORS[k].active
                        : "text-muted-foreground hover:bg-muted"}`}>
              {data.strategies[k]?.label || k}
              <span className="ml-1.5 font-mono opacity-70">
                {pct(data.strategies[k]?.stats?.total_return, 0)}
              </span>
            </button>
          ))}
        </div>
      )}

      {!data && !running && (
        <Card>
          <CardContent className="py-16 text-center text-sm text-muted-foreground">
            No backtest results yet — hit "Run backtest" to replay all four sleeves.
          </CardContent>
        </Card>
      )}
      {!data && running && (
        <Card>
          <CardContent className="py-16 text-center text-sm text-muted-foreground animate-pulse">
            Backtest running… {status?.step}
          </CardContent>
        </Card>
      )}

      {current && <Sleeve s={current} sleeveKey={sleeve} />}
    </div>
  );
}
